import React, { useEffect, useState } from "react";
import { Container, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";
import "./Main.scss";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const MainMenu = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hoverTimeouts, setHoverTimeouts] = useState({});
  const symbol = useSelector((state) => state.security.mainDefaultSecurity);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      setIsDevelopmentMode(true);
    }

    // Set initial active dropdown based on current path
    const activeDropdownFromPath = getActiveDropdownFromPath();
    if (activeDropdownFromPath) {
      setActiveDropdown(activeDropdownFromPath);
    }

    // Clean up any timeouts when component unmounts
    return () => {
      Object.values(hoverTimeouts).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  // Handle mouse enter for dropdown
  const handleDropdownMouseEnter = (id) => {
    // Cancel any existing timeout for this dropdown
    if (hoverTimeouts[id]) {
      clearTimeout(hoverTimeouts[id]);
    }

    // Set a small delay before showing the dropdown
    const timeout = setTimeout(() => {
      setActiveDropdown(id);
    }, 100);

    // Store the timeout ID
    setHoverTimeouts((prev) => ({ ...prev, [id]: timeout }));
  };

  // Handle mouse leave for dropdown
  const handleDropdownMouseLeave = (id) => {
    // Cancel any existing timeout for this dropdown
    if (hoverTimeouts[id]) {
      clearTimeout(hoverTimeouts[id]);
    }

    // Reset active dropdown
    setActiveDropdown(null);
  };

  // Handle dropdown item click
  const handleDropdownItemClick = () => {
    setActiveDropdown(null);
  };

  // Check if a dropdown contains the current path
  const isDropdownActive = (pathPrefix) => {
    return location.pathname.startsWith(`/${pathPrefix}`);
  };

  // Map paths to their parent dropdown
  const getActiveDropdownFromPath = () => {
    if (location.pathname.includes('marketSummary') ||
      location.pathname.includes('indexAnalysis') ||
      location.pathname.includes('stockIndexEffect') ||
      location.pathname.includes('globalIndices')) {
      return "markets";
    }

    if (location.pathname.includes('dividends')) {
      return "dividends";
    }

    return null;
  };

  // Prevent default on dropdown toggle click to keep hover behavior
  const handleToggleClick = (id) => (e) => {
    if (e.target.classList.contains("dropdown-toggle") || e.target.closest(".nav-link-text")) {
      e.preventDefault();
      e.stopPropagation();

      // Toggle the dropdown: if it's already open, close it; otherwise, open it
      setActiveDropdown(prevState => prevState === id ? null : id);
    }
  };

  return (
    <>
      <Navbar expand="lg" className="main-navbar">
        <Container className="p-0">
          <Navbar.Collapse id="main-navbar-nav">
            <Nav>
              <NavLink
                to="watchlist"
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.watchlist")}
              </NavLink>

              <NavLink
                to="standouts"
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.standouts")}
              </NavLink>

              <NavDropdown
                title={
                  <span className="nav-link-text">
                    {t("mainNavbar.markets")}{" "}
                    <i className="fas fa-chevron-down"></i>
                  </span>
                }
                id="nav-dropdown-piyasa"
                className={`nav-dropdown roboto-medium-16 ${activeDropdown === "markets" ? "show" : ""}`}
                show={activeDropdown === "markets"}
                onMouseEnter={() => handleDropdownMouseEnter("markets")}
                onMouseLeave={() => handleDropdownMouseLeave("markets")}
                onClick={handleToggleClick("markets")}
              >
                <NavDropdown.Item eventKey="3.1" as={NavLink} to="marketSummary" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.marketSummary")}
                </NavDropdown.Item>
                <NavDropdown.Item eventKey="3.2" as={NavLink} to="indexAnalysis" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.indexAnalysis")}
                </NavDropdown.Item>
                <NavDropdown.Item eventKey="3.3" as={NavLink} to="stockIndexEffect" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.stockIndexEffect")}
                </NavDropdown.Item>
                <NavDropdown.Item eventKey="3.4" as={NavLink} to="globalIndices" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.globalIndices")}
                </NavDropdown.Item>
              </NavDropdown>

              <NavLink
                to={`/StockAnalysis/summary/${symbol}`}
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.stockAnalysis")}
              </NavLink>

              <NavDropdown
                title={
                  <span className="nav-link-text">
                    {t("mainNavbar.dividend")}{" "}
                    <i className="fas fa-chevron-down"></i>
                  </span>
                }
                id="nav-dropdown-dividend"
                className={`nav-dropdown roboto-medium-16 ${activeDropdown === "dividends" ? "show" : ""}`}
                show={activeDropdown === "dividends"}
                onMouseEnter={() => handleDropdownMouseEnter("dividends")}
                onMouseLeave={() => handleDropdownMouseLeave("dividends")}
                onClick={handleToggleClick("dividends")}
              >
                <NavDropdown.Item as={NavLink} to="dividends/dividendCalendar" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.dividendCalendar")}
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="dividends/dividendExpectations" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.dividendExpectations")}
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="dividends/dividendFiltering" onClick={handleDropdownItemClick}>
                  {t("mainNavbar.dividendFiltering")}
                </NavDropdown.Item>
                {/* Getiri Hesap Makinesi gizlendi */}
              </NavDropdown>

              <NavLink
                to="/news"
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.news")}
              </NavLink>

              <NavLink
                to="technicalAnalysis"
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.technicalAnalysis")}
              </NavLink>

              <NavLink
                to="/personalSorting"
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.personalSorting")}
              </NavLink>

              <NavLink
                to="/stockFiltering"
                className={({ isActive }) =>
                  isActive
                    ? "nav-link active-nav-link-border roboto-medium-16"
                    : "nav-link roboto-medium-16"
                }
              >
                {t("mainNavbar.stockFiltering")}
              </NavLink>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
};

export default MainMenu;
